import { useCallback, useEffect, useRef } from 'react'
import { useAuth } from '@/providers/auth'
import { useWebSocket } from '@/hooks/use-websocket'
import { getStoredJwtToken, refreshJwtToken } from '@/api/services/auth.service'
import { getStoredConnectionId } from '@/providers/websocket'
import { logger } from '@/utils/logger'

const WS_AUTH_TIMEOUT_MS = 10_000
const WS_AUTH_MAX_RETRIES = 2

/**
 * WebSocket Auth Handler
 *
 * Sends the auth event when the socket connects, then waits for a connectionId.
 * If the server doesn't return one within the timeout, the handler refreshes the
 * JWT and retries.  After `WS_AUTH_MAX_RETRIES` without success it gives up
 * (API-level 401 or the proactive scheduler will handle forced logout).
 */
export function WebSocketAuthHandler() {
  const { token } = useAuth()
  const { isConnected, connectionId, sendMessage } = useWebSocket()
  const retryCountRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  // Send auth and arm the timeout on connect / token change
  useEffect(() => {
    if (!isConnected) return

    retryCountRef.current = 0

    const sendAuth = (jwt: string) => {
      sendMessage({ event: 'auth', token: jwt })

      clearTimer()
      timerRef.current = setTimeout(async () => {
        timerRef.current = null

        if (getStoredConnectionId()) return

        if (retryCountRef.current >= WS_AUTH_MAX_RETRIES) {
          logger.warn('WebSocket auth: max retries reached without receiving connectionId')
          return
        }

        retryCountRef.current++
        logger.warn(
          `WebSocket auth: no connectionId after ${WS_AUTH_TIMEOUT_MS}ms — ` +
            `refreshing token (retry ${retryCountRef.current}/${WS_AUTH_MAX_RETRIES})`
        )

        const refreshed = await refreshJwtToken()

        if (refreshed) {
          sendAuth(refreshed)
        } else {
          logger.warn('WebSocket auth: token refresh failed during retry')
        }
      }, WS_AUTH_TIMEOUT_MS)
    }

    const authToken = getStoredJwtToken()

    if (authToken) sendAuth(authToken)

    return clearTimer
  }, [isConnected, token, sendMessage, clearTimer])

  // Cancel the timeout as soon as we receive a connectionId
  useEffect(() => {
    if (connectionId) clearTimer()
  }, [connectionId, clearTimer])

  return null
}
